/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { isValidEmail } from "../../utils/is-valid-email"

/**
 * Validation or submission errors shown by the Create User form.
 *
 * Field errors are attached to the relevant input; `form` is used for errors
 * that apply to the form as a whole.
 */
export type CreateUserErrors = {
    name?: string
    username?: string
    form?: string
}

/**
 * Result of validating the Create User form.
 *
 * Contains the trimmed values ready for submission and any validation errors.
 */
export type CreateUserValidation = {
    name: string
    username: string
    errors: CreateUserErrors
}

/**
 * Trims and validates the Create User form values before submission.
 *
 * @param name - Common name.
 * @param username - Username, expected to be an email address.
 * @returns The trimmed values together with any validation errors.
 */
export function validateCreateUser(name: string, username: string): CreateUserValidation {
    const trimmedName = name.trim()
    const trimmedUsername = username.trim()

    const errors: CreateUserErrors = {}
    if (!trimmedName) {
        errors.name = "Name is required."
    }
    if (!trimmedUsername) {
        errors.username = "Username is required."
    } else if (!isValidEmail(trimmedUsername)) {
        errors.username = "Please enter a valid email address."
    }
    return { name: trimmedName, username: trimmedUsername, errors }
}

/**
 * Maps a failed create-user response to an error shown by the Create User form.
 *
 * @remarks
 * The API uses 400 for all request-validation failures and does not expose the
 * specific reason outside its dev profile. The form validates the other known
 * causes before submission, so a 400 is treated as a duplicate username.
 * This is necessarily best-effort until the API provides a specific error code.
 */
export function createUserError(status?: number): CreateUserErrors {
    return status === 400
        ? { username: "This username is already in use." }
        : { form: "Could not create the user. Please try again." }
}
